import { useState } from 'react';
import { NoteEditor } from './NoteEditor';
import { NoteContextPanel } from './NoteContextPanel';
import type {
  MemoryBankNoteMetadata,
  MemoryBankSupportedEntityTypes,
} from '@erp/rpc';
import { REMITTANCE_MEMORY_CANDIDATE_SCOPE_METADATA_KEY } from '@erp/shared-types';

export interface NoteEditFormProps {
  /**
   * Initial note content to populate the editor
   */
  initialValue?: string;

  /**
   * Metadata to display as context
   */
  metadata?: Partial<MemoryBankNoteMetadata>;

  /**
   * Whether to show the metadata context section
   */
  showMetadata?: boolean;

  /**
   * Placeholder text for the editor
   */
  placeholder?: string;

  /**
   * Height of the editor in pixels
   */
  height?: number;

  /**
   * Whether the form is currently submitting
   */
  isSubmitting?: boolean;

  /**
   * Called when user saves the note
   */
  onSave: (
    content: string,
    filteredMetadata: Partial<MemoryBankNoteMetadata>,
  ) => void | Promise<void>;

  /**
   * Called when user cancels editing
   */
  onCancel: () => void;

  /**
   * Additional CSS classes
   */
  className?: string;

  /**
   * Entity type for context display
   */
  entityType?: MemoryBankSupportedEntityTypes;

  /**
   * Entity ID for context display
   */
  entityId?: string;

  /**
   * Whether metadata pills can be removed
   */
  allowMetadataRemoval?: boolean;
}

export function NoteEditForm({
  initialValue,
  metadata,
  showMetadata = true,
  placeholder = '',
  height,
  isSubmitting,
  onSave,
  onCancel,
  className,
  entityType,
  entityId,
  allowMetadataRemoval = true,
}: NoteEditFormProps) {
  const [removedMetadataKeys, setRemovedMetadataKeys] = useState<Set<string>>(
    new Set(),
  );
  // Filter metadata: remove null, undefined, and removed keys
  const filteredMetadata = metadata
    ? Object.fromEntries(
        Object.entries(metadata).filter(
          ([key, value]) =>
            value !== undefined &&
            value !== null &&
            !removedMetadataKeys.has(key),
        ),
      )
    : {};

  const contextMetadata = Object.fromEntries(
    Object.entries(filteredMetadata).filter(
      ([key]) => key !== REMITTANCE_MEMORY_CANDIDATE_SCOPE_METADATA_KEY,
    ),
  );
  const handleRemoveMetadata = (key: string) => {
    setRemovedMetadataKeys((prev) => new Set(prev).add(key));
  };

  const handleSave = (content: string) => {
    return onSave(content, filteredMetadata);
  };

  return (
    <div className={className}>
      <NoteEditor
        initialValue={initialValue}
        onSave={handleSave}
        onCancel={onCancel}
        isSubmitting={isSubmitting}
        placeholder={placeholder}
        height={height}
      />

      <NoteContextPanel
        show={showMetadata}
        metadata={contextMetadata}
        entityType={entityType}
        entityId={entityId}
        allowMetadataRemoval={allowMetadataRemoval}
        onRemoveMetadata={handleRemoveMetadata}
      />
    </div>
  );
}
